import {
  FieldValues,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  IFormField,
  origins,
  IAuthenticationExternalApiData,
} from "@pbm/registration-policies/contracts";
import { Grid, Dialog, DialogActions } from "@material-ui/core";
import {
  AutocompleteField,
  Button,
  CheckBoxField,
  GraphQLValidationErrors,
  TextField,
  Title,
} from "@funcional/react-ui";
import { useState } from "react";
import { DialogTitle } from "@material-ui/core";
import { DialogContent } from "@material-ui/core";
import { FormFieldForm } from "./FormFieldForm";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { FormFieldList } from "./FormFieldList";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { IGraphQLError } from "@common/contracts";
import { useI18n } from "@common/helpers";

const containerSpacing = 3;

const schema = yup.object().shape({
  description: yup.string().required().min(3, "deve ter pelo menos 3 letras"),
});

export default function RegistrationPolicyForm({
  onSubmit,
  defaults,
  authenticationExternalApi,
  isLoading,
  graphQLErrors,
}: {
  onSubmit: SubmitHandler<FieldValues>;
  defaults?: any;
  authenticationExternalApi?: IAuthenticationExternalApiData;
  isLoading?: boolean;
  graphQLErrors?: IGraphQLError[];
}) {
  const { intlEnum } = useI18n();
  const [displayFormFieldModal, setDisplayFormFieldModal] = useState(false);
  const [formField, setFormField] = useState();
  const {
    control,
    handleSubmit,
    watch,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: defaults,
    resolver: yupResolver(schema),
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "formFields",
    keyName: "code",
  });

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove,
  } = useFieldArray({
    control,
    name: "header",
  });
  const {
    fields: formFieldsApi,
    append: formAppendApi,
    remove: formRemoveApi,
  } = useFieldArray({
    control,
    name: "form",
  });

  const editFormField = (row: any) => {
    setFormField(row.original);
    setDisplayFormFieldModal(true);
  };
  const deleteFormField = (row: any) => {
    if (window.confirm("Tem certeza de que deseja excluir este item?")) {
      remove(row.index);
    }
  };
  const addFormField = () => {
    editFormField({
      code: "",
      label: "",
      type: "STRING",
      description: "",
      display: "REQUIRED",
      defaultValue: "",
      entityField: "",
      options: [],
      externalApiField: "",
    });
  };

  const onSubmitFormField = (values: IFormField) => {
    const index = fields.findIndex(
      (item: IFormField) => item.code === values.code
    );

    if (index > -1) {
      update(index, values as never);
      setDisplayFormFieldModal(false);
      return;
    }

    append([values as never]);
    setDisplayFormFieldModal(false);
  };

  return (
    <>
      <Dialog
        open={displayFormFieldModal}
        onClose={() => setDisplayFormFieldModal(false)}
      >
        <DialogTitle>Editar Campo de Formulário</DialogTitle>
        <DialogContent>
          <FormFieldForm
            defaults={formField}
            onCancel={() => setDisplayFormFieldModal(false)}
            onSubmit={onSubmitFormField}
            enabledApiExternal={watch("enabledApiExternal")}
          />
        </DialogContent>
        <DialogActions>&nbsp;</DialogActions>
      </Dialog>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <GraphQLValidationErrors graphQLErrors={graphQLErrors} />

        <Grid container spacing={containerSpacing}>
          <Grid item xs={12}>
            <TextField
              required
              name="description"
              label="Descrição"
              control={control}
              defaultValue={defaults?.description}
              errorMessage={errors?.description?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CheckBoxField
              control={control}
              name="requiresRegistration"
              label="Exige inscrição do beneficiário?"
              defaultChecked={defaults?.requiresRegistration}
              errorMessage={errors?.requiresRegistration?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CheckBoxField
              control={control}
              name="requiresPrescription"
              label="Exige receita médica na compra?"
              defaultChecked={defaults?.requiresPrescription}
              errorMessage={errors?.requiresPrescription?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CheckBoxField
              control={control}
              name="requiresContactInformation"
              label="Exige uma forma de contato?"
              defaultChecked={defaults?.requiresContactInformation}
              errorMessage={errors?.requiresContactInformation?.message}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CheckBoxField
              control={control}
              name="enabledApiExternal"
              label="Consulta Api Externa"
              defaultChecked={defaults?.enabledApiExternal}
              errorMessage={errors?.enabledApiExternal?.message}
            />
          </Grid>

          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              <div className="flex justify-between w-full">
                <Title level={3}>Url Api</Title>
              </div>
              <TextField
                required={watch("enabledApiExternal") === true ? true : false}
                control={control}
                name="url"
                label="Campo da URL"
                defaultValue={authenticationExternalApi?.url}
                errorMessage={errors?.url?.message}
              />
            </Grid>
          ) : null}
          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              <div className="flex justify-between w-full">
                <Title level={3}>Method</Title>
              </div>
              <TextField
                required={watch("enabledApiExternal") === true ? true : false}
                control={control}
                name="method"
                label="Campo Metodo"
                defaultValue={authenticationExternalApi?.method}
                errorMessage={errors?.method?.message}
              />
            </Grid>
          ) : null}

          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              <div className="flex justify-between w-full">
                <Title level={3}>Token</Title>
              </div>
              <TextField
                control={control}
                name="token"
                label="Campo Metodo"
                defaultValue={authenticationExternalApi?.token}
              />
            </Grid>
          ) : null}

          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              {headerFields.map((item, index) => {
                return (
                  <ul className="space-x-12 space-y-12">
                    <li key={item.id}>
                      <div className="flex justify-between w-full">
                        <Title level={3}>Header: {index + 1} </Title>
                      </div>

                      <Grid item xs={12} md={12}>
                        <div>
                          <TextField
                            label="Key"
                            defaultValue={defaults?.header[index]?.key}
                            {...register(`header.${index}.key`)}
                          />
                        </div>
                      </Grid>

                      <br></br>

                      <Grid item xs={12} md={12}>
                        <div>
                          <TextField
                            label="Value"
                            defaultValue={defaults?.header[index]?.value}
                            {...register(`header.${index}.value`)}
                          />
                        </div>
                      </Grid>

                      <br></br>

                      <Grid item xs={12}>
                        <Button
                          type="button"
                          title="Delete"
                          onClick={() => headerRemove(index)}
                        >
                          <RemoveIcon />
                        </Button>
                      </Grid>
                      <br></br>
                    </li>
                  </ul>
                );
              })}
            </Grid>
          ) : null}

          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              <Button
                type="button"
                title="Add"
                onClick={() => {
                  headerAppend({});
                }}
              >
                <AddIcon />
                &nbsp; Add Header
              </Button>
            </Grid>
          ) : null}

          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              {formFieldsApi.map((item, index) => {
                return (
                  <ul className="space-x-12 space-y-12">
                    <li key={item.id}>
                      <div className="flex justify-between w-full">
                        <Title level={3}>Form: {index + 1} </Title>
                      </div>
                      <Grid item xs={12} md={12}>
                        <div>
                          <TextField
                            label="type"
                            defaultValue={defaults?.form[index]?.type}
                            {...register(`form.${index}.type`)}
                          />
                        </div>
                      </Grid>
                      <br></br>
                      <Grid item xs={12} md={12}>
                        <div>
                          <TextField
                            label="Key"
                            defaultValue={defaults?.form[index]?.key}
                            {...register(`form.${index}.key`)}
                          />
                        </div>
                      </Grid>
                      <br></br>
                      <Grid item xs={12} md={12}>
                        <div>
                          <TextField
                            label="Value"
                            defaultValue={defaults?.form[index]?.value}
                            {...register(`form.${index}.value`, {})}
                          />
                        </div>
                      </Grid>
                      <br></br>
                      <Grid item xs={12}>
                        <Button
                          type="button"
                          title="Delete"
                          onClick={() => formRemoveApi(index)}
                        >
                          <RemoveIcon />
                        </Button>
                      </Grid>
                      <br></br>
                    </li>
                  </ul>
                );
              })}
            </Grid>
          ) : null}
          {watch("enabledApiExternal") ? (
            <Grid item xs={12}>
              <Button
                type="button"
                title="Add"
                onClick={() => {
                  formAppendApi({});
                }}
              >
                <AddIcon />
                &nbsp; Add Form
              </Button>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <AutocompleteField
              label="Origens Permitidas"
              name="allowedOrigins"
              control={control}
              options={origins.map((value) => ({
                value,
                label: intlEnum("authorization_origin", value) as string,
              }))}
              defaultValue={defaults?.allowedOrigins?.map((value) => ({
                label: intlEnum("authorization_origin", value) as string,
                value,
              }))}
              errorMessage={
                (errors?.allowedOrigins?.length || 0) > 0
                  ? errors.allowedOrigins?.join(", ")
                  : undefined
              }
            />
          </Grid>
          {fields ? (
            <Grid item xs={12}>
              <div className="flex items-center justify-between w-full">
                <Title level={2}>Campos do Formulário de Cadastro</Title>
                <div>
                  <Button
                    type="button"
                    startIcon={<AddIcon />}
                    size="small"
                    disabled={isLoading}
                    onClick={addFormField}
                    title="Criar novo campo no formulário de cadastro"
                  >
                    Novo campo
                  </Button>
                </div>
              </div>
              <FormFieldList
                formFields={fields}
                editFormField={editFormField}
                deleteFormField={deleteFormField}
              />
            </Grid>
          ) : null}
          <Grid item xs={12}>
            <Button color="primary" type="submit" disabled={isLoading}>
              Salvar
            </Button>
          </Grid>
        </Grid>
      </form>
    </>
  );
}
